import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Clock,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Mail,
  CheckCircle2,
  Calendar,
  Users,
  Building2,
  Play,
  Loader2
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SuperAdminExpirationsDashboard() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState([]);
  const [period, setPeriod] = useState('30');
  const [lastCheck, setLastCheck] = useState(null);

  const fetchExpiringSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/superadmin/expiring-subscriptions?days=${period}`);
      setExpiringSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des expirations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchExpiringSubscriptions();
  }, [fetchExpiringSubscriptions]);

  const handleCheckExpirations = async () => {
    try {
      setProcessing(true);
      const response = await api.post('/superadmin/check-expirations');
      const { summary, details } = response.data;
      
      setLastCheck({
        time: new Date().toLocaleString('fr-FR'),
        subscriptionsChecked: summary.subscriptionsChecked,
        notificationsSent: summary.notificationsSent,
        accountsDowngraded: summary.accountsDowngraded,
        errors: summary.errors,
        details: details
      });
      
      if (summary.accountsDowngraded > 0) {
        toast.warning(`${summary.accountsDowngraded} compte(s) rétrogradé(s) automatiquement`);
      } else if (summary.notificationsSent > 0) {
        toast.success(`${summary.notificationsSent} notification(s) envoyée(s)`);
      } else {
        toast.success('Aucune action nécessaire');
      }
      
      // Refresh the list
      fetchExpiringSubscriptions();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const getSeverityBadge = (daysRemaining) => {
    if (daysRemaining < 0) {
      return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" /> Expiré</Badge>;
    } else if (daysRemaining <= 1) {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Urgent</Badge>;
    } else if (daysRemaining <= 15) {
      return <Badge variant="warning" className="gap-1 bg-orange-500"><Clock className="w-3 h-3" /> Bientôt</Badge>;
    } else {
      return <Badge variant="outline" className="gap-1"><Calendar className="w-3 h-3" /> {daysRemaining}j</Badge>;
    }
  };

  const getNotificationStatus = (sub) => {
    const notifications = [];
    if (sub.notified30d) notifications.push('30j');
    if (sub.notified15d) notifications.push('15j');
    if (sub.notified1d) notifications.push('1j');
    
    if (notifications.length === 0) {
      return <span className="text-slate-400">Aucune</span>;
    }
    
    return (
      <div className="flex gap-1">
        {notifications.map(n => (
          <Badge key={n} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            <Mail className="w-3 h-3 mr-1" />{n}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Gestion des Expirations</h2>
          <p className="text-slate-600">Suivez et gérez les abonnements qui arrivent à expiration</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 prochains jours</SelectItem>
              <SelectItem value="15">15 prochains jours</SelectItem>
              <SelectItem value="30">30 prochains jours</SelectItem>
              <SelectItem value="60">60 prochains jours</SelectItem>
              <SelectItem value="90">90 prochains jours</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchExpiringSubscriptions} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button onClick={handleCheckExpirations} disabled={processing}>
            {processing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Vérifier & Notifier
          </Button>
        </div>
      </div>

      {/* Last Check Summary */}
      {lastCheck && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Dernière vérification : {lastCheck.time}</p>
                  <p className="text-sm text-blue-700">
                    {lastCheck.subscriptionsChecked} abonnements vérifiés • 
                    {lastCheck.notificationsSent} notifications envoyées • 
                    {lastCheck.accountsDowngraded} comptes rétrogradés
                    {lastCheck.errors > 0 && <span className="text-red-600"> • {lastCheck.errors} erreur(s)</span>}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Details */}
            {lastCheck.details && (lastCheck.details.notifications_sent?.length > 0 || lastCheck.details.accounts_downgraded?.length > 0) && (
              <div className="mt-4 space-y-2">
                {lastCheck.details.notifications_sent?.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium text-blue-800">Notifications envoyées :</span>
                    <ul className="ml-4 text-blue-700">
                      {lastCheck.details.notifications_sent.map((n, i) => (
                        <li key={i}>• {n.companyName} ({n.daysRemaining}j restants)</li>
                      ))}
                    </ul>
                  </div>
                )}
                {lastCheck.details.accounts_downgraded?.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium text-orange-800">Comptes rétrogradés :</span>
                    <ul className="ml-4 text-orange-700">
                      {lastCheck.details.accounts_downgraded.map((d, i) => (
                        <li key={i}>• {d.companyName} (expiré le {new Date(d.expiredAt).toLocaleDateString('fr-FR')})</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {expiringSubscriptions.filter(s => s.daysRemaining < 0).length}
                </p>
                <p className="text-sm text-slate-500">Expirés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {expiringSubscriptions.filter(s => s.daysRemaining >= 0 && s.daysRemaining <= 7).length}
                </p>
                <p className="text-sm text-slate-500">Cette semaine</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {expiringSubscriptions.filter(s => s.daysRemaining > 7 && s.daysRemaining <= 15).length}
                </p>
                <p className="text-sm text-slate-500">Dans 8-15 jours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {expiringSubscriptions.filter(s => s.daysRemaining > 15).length}
                </p>
                <p className="text-sm text-slate-500">Dans 16+ jours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Abonnements expirants ({expiringSubscriptions.length})
          </CardTitle>
          <CardDescription>
            Abonnements PRO qui expirent dans les {period} prochains jours
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : expiringSubscriptions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p className="font-medium">Aucun abonnement n'expire dans cette période</p>
              <p className="text-sm">Tous les comptes PRO sont en règle</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date d'expiration</TableHead>
                  <TableHead>Jours restants</TableHead>
                  <TableHead>Utilisateurs</TableHead>
                  <TableHead>Notifications</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expiringSubscriptions.map((sub) => (
                  <TableRow key={sub.companyId} className={sub.daysRemaining < 0 ? 'bg-red-50' : sub.daysRemaining <= 7 ? 'bg-orange-50' : ''}>
                    <TableCell>
                      <div className="font-medium">{sub.companyName}</div>
                      <div className="text-xs text-slate-500">{sub.companyId.slice(0, 8)}...</div>
                    </TableCell>
                    <TableCell>{getSeverityBadge(sub.daysRemaining)}</TableCell>
                    <TableCell>
                      {new Date(sub.planExpiresAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>
                      <span className={`font-bold ${
                        sub.daysRemaining < 0 ? 'text-red-600' : 
                        sub.daysRemaining <= 7 ? 'text-orange-600' : 
                        sub.daysRemaining <= 15 ? 'text-yellow-600' : 'text-slate-600'
                      }`}>
                        {sub.daysRemaining < 0 ? `${Math.abs(sub.daysRemaining)}j dépassé` : `${sub.daysRemaining} jours`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-slate-400" />
                        {sub.userCount || 0}
                      </div>
                    </TableCell>
                    <TableCell>{getNotificationStatus(sub)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-slate-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-slate-600" />
            </div>
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-900 mb-1">Comment fonctionne le système d'expiration ?</p>
              <ul className="space-y-1">
                <li>• Les notifications sont envoyées automatiquement à <strong>30 jours</strong>, <strong>15 jours</strong> et <strong>1 jour</strong> avant expiration</li>
                <li>• Les comptes expirés sont automatiquement <strong>rétrogradés vers le plan Gratuit</strong></li>
                <li>• Cliquez sur <strong>"Vérifier & Notifier"</strong> pour déclencher manuellement les notifications et rétrogradations</li>
                <li>• Configurez un <strong>cron job</strong> pour appeler <code>POST /api/superadmin/check-expirations</code> quotidiennement</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
